import styled from 'styled-components';
import {
  AppToaster,
  TOAST_STYLE,
  TOASTER_TOP_BELOW_STORE_HEADER,
} from './components/toast/AppToaster';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import { NutritionalInfo } from './components/nutritional-info/NutritionalInfo';
import { DeliveryApps } from './components/delivery-apps/DeliveryApps';
import { CustomerReviewsSection } from './components/customer-reviews';
import WhatsAppButton from './components/whatsapp/WhatsAppButton';
import { CookieConsentBanner } from './components/cookie-consent';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import CheckoutPage from './pages/CheckoutPage';
import SuccessPage from './pages/SuccessPage';
import FailurePage from './pages/FailurePage';
import NotFoundPage from './pages/NotFoundPage';
import { lazy, Suspense, useEffect, useRef } from 'react';

const ReviewPage = lazy(() => import('./pages/ReviewPage'));
import { Helmet } from 'react-helmet-async';
import { eventHub } from './lib/tracking/event-hub';
import { PixelLoader } from './lib/tracking/PixelLoader';
import { useStoreInfo } from './store/useStoreInfo';
import { normalizeStoreAssetUrl } from './lib/mediaUrl';
import { captureFirstTouchFromWindow } from './lib/attributionFirstTouch';
import { runWhenAnalyticsConsented } from './lib/tracking/cookieConsent';

const AppContainer = styled.div`
  width: 100%;
`;

const ContentArea = styled.main<{ $isFullWidth?: boolean }>`
  max-width: ${props => props.$isFullWidth ? '100%' : '1200px'};
  margin: 0 auto;
  padding: ${props => props.$isFullWidth ? '0' : '20px'};
`;

/** إخفاء الهيدر بصرياً بدل إزالته من الـ DOM لتجنب NotFoundError (insertBefore) عند تغيير المسار */
const HeaderWrapper = styled.div<{ $visible?: boolean }>`
  display: ${props => (props.$visible ? 'block' : 'none')};
`;

/** واجهة fallback عند وقوع خطأ (مثل NotFoundError) بدل تعطل التطبيق */
function RouteErrorFallback() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: '2rem', textAlign: 'center', direction: 'rtl' }}>
      <p style={{ marginBottom: '1rem' }}>حدث خطأ غير متوقع. جرب تحديث الصفحة أو العودة للرئيسية.</p>
      <button type="button" onClick={() => navigate('/')} style={{ padding: '0.5rem 1rem' }}>
        العودة للرئيسية
      </button>
    </div>
  );
}

function App() {
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname.startsWith('/product');
  /** الصفحة الرئيسية فقط (بدون /product) لقسم التقييمات العامة */
  const isStorefrontRoot = location.pathname === '/';
  const isReviewPage = location.pathname.startsWith('/review/');
  const isNotFoundPage =
    location.pathname !== '/' &&
    location.pathname !== '/checkout' &&
    !location.pathname.startsWith('/success') &&
    location.pathname !== '/failure' &&
    !location.pathname.startsWith('/product/') &&
    !isReviewPage;
  const lastTrackedPath = useRef<string>('');
  const { data: storeData, fetchStoreData } = useStoreInfo();
  const storeName = storeData?.name || 'اطلب';

  // أول لمسة (UTM + referrer) — مرة واحدة في التخزين المحلي
  useEffect(() => {
    captureFirstTouchFromWindow();
  }, [location.search]);

  useEffect(() => {
    if (!isReviewPage) fetchStoreData();
  }, [fetchStoreData, isReviewPage]);

  // بعد تغيير الشعار/البنر من التطبيق، إعادة جلب `/api/store` عند العودة للتبويب (المتجر كان يُحمَّل مرة واحدة فقط)
  useEffect(() => {
    if (isReviewPage) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchStoreData(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchStoreData, isReviewPage]);

  // دالة للحصول على اسم الصفحة وعنوانها بناءً على المسار واسم المتجر (ديناميكي)
  const getPageInfo = (pathname: string, siteName: string) => {
    const suffix = ` - ${siteName}`;
    const pageMap: Record<string, { name: string; title: string }> = {
      '/': { name: 'Home', title: `الصفحة الرئيسية${suffix}` },
      '/checkout': { name: 'Checkout', title: `إتمام الطلب${suffix}` },
      '/success': { name: 'Success', title: `تم الدفع بنجاح${suffix}` },
      '/failure': { name: 'Failure', title: `فشل الدفع${suffix}` },
      '/thankyou': { name: 'ThankYou', title: `شكراً لك${suffix}` },
    };

    if (pageMap[pathname]) {
      return pageMap[pathname];
    }
    if (pathname.startsWith('/success')) {
      return pageMap['/success'];
    }
    if (pathname.startsWith('/product')) {
      return { name: 'Product', title: `المنتج${suffix}` };
    }
    if (pathname.startsWith('/review/')) {
      return { name: 'Review', title: `تقييم الطلب${suffix}` };
    }

    const pageName = pathname.replace('/', '').replace(/-/g, ' ') || 'Home';
    return { name: pageName, title: `صفحة ${pageName}${suffix}` };
  };

  // تحديث عنوان الصفحة وإعادة التمرير إلى الأعلى عند تغيير المسار أو تحميل اسم المتجر
  useEffect(() => {
    window.scrollTo(0, 0);
    const pageInfo = getPageInfo(location.pathname, storeName);
    document.title = pageInfo.title;
  }, [location.pathname, storeName]);

  // وسوم Open Graph للمشاركة (واتساب، فيسبوك) - اسم المتجر وصورته
  const ogTitle = storeName;
  const ogDescription = storeData ? `تصفح واطلب من ${storeName}` : 'تصفح واطلب من متجرنا';
  const ogImage = (() => {
    const fallback =
      typeof window !== 'undefined' ? `${window.location.origin}/favicon.png` : '/favicon.png';
    if (!storeData?.logo) return fallback;
    const u = normalizeStoreAssetUrl(storeData.logo);
    if (!u) return fallback;
    if (/^https?:\/\//i.test(u)) return u;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${u.startsWith('/') ? '' : '/'}${u}`;
  })();

  // ✅ Page View Tracking: إرسال PAGE_VIEW فقط في الصفحة الرئيسية (Home)
  // يتم إرسال PAGE_VIEW إلى جميع المنصات (Pixel SDK + CAPI) لضمان الاتزان
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    runWhenAnalyticsConsented(() => {
      if (lastTrackedPath.current === location.pathname) {
        return;
      }
      lastTrackedPath.current = location.pathname;

      const pageInfo = getPageInfo(location.pathname, storeName);

      eventHub.pageView({
        page_name: pageInfo.name,
        page_title: pageInfo.title,
        page_category: 'Home',
      });
    });
  }, [location.pathname, storeName]);

  // فحص وجود Pixel IDs قبل تحميل PixelLoader — تخطّي صفحة التقييم
  const hasPixels = !isReviewPage && (import.meta.env.VITE_META_PIXEL_ID ||
                    import.meta.env.VITE_SNAPCHAT_PIXEL_ID ||
                    import.meta.env.VITE_TIKTOK_PIXEL_ID ||
                    import.meta.env.VITE_GA4_MEASUREMENT_ID ||
                    import.meta.env.VITE_YANDEX_COUNTER_ID);

  return (
    <AppContainer>
      <Helmet>
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      {hasPixels && <PixelLoader />}
      <AppToaster
        position="top-center"
        reverseOrder={false}
        containerStyle={
          isHomePage ? { top: TOASTER_TOP_BELOW_STORE_HEADER } : undefined
        }
        toastOptions={{
          duration: 4000,
          style: TOAST_STYLE,
          error: { duration: 5500 },
        }}
      />
      {/* إبقاء Header في الـ DOM وإخفاؤه بـ CSS لتجنب NotFoundError (insertBefore) عند تغيير المسار */}
      <HeaderWrapper $visible={isHomePage}>
        <Header />
      </HeaderWrapper>

      <ContentArea $isFullWidth={!isHomePage}>
        <Sentry.ErrorBoundary fallback={<RouteErrorFallback />} showDialog={false}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/success/:token" element={<SuccessPage />} />
            <Route path="/failure" element={<FailurePage />} />
            <Route path="/review/:token" element={<Suspense fallback={null}><ReviewPage /></Suspense>} />
            <Route path="/thankyou" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </ContentArea>

      {isHomePage && <DeliveryApps />}
      {isStorefrontRoot && <CustomerReviewsSection />}
      {isHomePage && <NutritionalInfo />}
      {!isNotFoundPage && <Footer />}
      <WhatsAppButton />
      <CookieConsentBanner />
    </AppContainer>
  )
}

export default App
